import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import styles from './SplashPage.module.scss'
import { SplashPageCTA } from '@/components/static/SplashPageSharedComponents/SplashPageCTA/SplashPageCTA'
import { SplashPageTeam } from './SplashPageTeam/SplashPageTeam'
import { SplashTopSection } from '@/components/static/SplashPage/SplashPageTopSection/SplashPageTopSection'
import { SplashPageMiddleSection } from '@/components/static/SplashPage/SplashPageMiddleSection/SplashPageMiddleSection'
import { useIsMobileMode } from '@/hooks/static/useIsMobileMode'
import { useRouter } from 'next/router'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import Modal from 'antd/lib/modal/Modal'
import authenticatorStyles from '@/components/static/Authenticator/Authenticator.module.scss'
import { Authenticator } from '@/components/static/Authenticator/Authenticator'
import {
    AuthenticatorOpenPurpose,
    AuthenticatorRoutingProps,
    AuthenticatorTabName,
} from '@/components/static/Authenticator/Authenticator.types'
import { parsePurchaseLinkFromAsPath } from '@/utils/purchaseLink'
import { SplashPageProps } from '@/components/sharedFetchStaticProps/fetchSplashPageProps'
import { ErrorBoundary } from '@/components/static/ErrorBoundary/ErrorBoundary'

export const SplashPage: React.FC<SplashPageProps> = (props): ReactElement => {
    const router = useRouter()
    const [isLoginModalVisible, setIsLoginModalVisible] =
        useState<boolean>(false)
    const [loginModalRoutingPurpose, setLoginModalRoutingPurpose] =
        useState<AuthenticatorRoutingProps>(null)

    useEffect(() => {
        if (!router) {
            return
        }
        // `/?purchase=<courseName>`: the home page is only served logged out,
        // so open sign-up with a purchase intent before the query is cleaned
        // off the URL below (logged in, middleware sends `/` to the dashboard,
        // which forwards the same link to checkout). Read from asPath: on this
        // statically rendered page router.query is still empty on the first
        // render, and the replace below runs before router.isReady flips.
        const purchaseLinkIntent = parsePurchaseLinkFromAsPath(router.asPath)
        if (purchaseLinkIntent) {
            setLoginModalRoutingPurpose({
                purpose: AuthenticatorOpenPurpose.PURCHASE,
                courseName: purchaseLinkIntent.courseName,
                accessTypes: purchaseLinkIntent.accessTypes,
            })
            setIsLoginModalVisible(true)
            // leave the URL alone: the clean-up replace below raced the modal
            // (Next aborted the in-flight route change and the modal closed)
            return
        }
        // normalise the path only; a query string on `/` is harmless
        const pathWithoutQuery = router.asPath.split('?')[0]
        if (
            pathWithoutQuery !== UiLayoutConstant.SPLASH_PAGE &&
            pathWithoutQuery !== UiLayoutConstant.SPLASH_PAGE_LOGGED_IN
        ) {
            router.replace({ pathname: UiLayoutConstant.SPLASH_PAGE })
        }
    }, [router])

    const isMobileMode = useIsMobileMode(false)
    const authenticatorModal = useMemo((): ReactElement => {
        return (
            <Modal
                title={null}
                centered
                footer={null}
                open={isLoginModalVisible}
                onCancel={() => setIsLoginModalVisible(false)}
                className={authenticatorStyles.authenticatorModal}
            >
                <Authenticator
                    defaultTab={AuthenticatorTabName.signUp}
                    setIsModalVisible={setIsLoginModalVisible}
                    routingPurpose={loginModalRoutingPurpose}
                    courseConfigType={props.courseConfigType}
                />
            </Modal>
        )
    }, [isLoginModalVisible, loginModalRoutingPurpose, props.courseConfigType])
    return (
        <div className={styles.entirePageBackground}>
            {authenticatorModal}
            <div
                className={styles.splashPageBackground}
                id={'cypress-splash-page'}
            >
                <SplashTopSection
                    {...props}
                    setIsLoginModalVisible={setIsLoginModalVisible}
                    setLoginModalRoutingPurpose={setLoginModalRoutingPurpose}
                />
                <ErrorBoundary fallback={<div />}>
                    <SplashPageMiddleSection
                        isMobileMode={isMobileMode}
                        courseConfigType={props.courseConfigType}
                    />
                </ErrorBoundary>
                <ErrorBoundary fallback={<div />}>
                    <SplashPageTeam courseConfigType={props.courseConfigType} />
                </ErrorBoundary>
                <SplashPageCTA />
            </div>
        </div>
    )
}
